import { useEffect, useState } from 'react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Database, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ProbeResult {
  available: boolean
  reason?: string
  tables?: { name: string; sql: string }[]
}

interface PageResult {
  rows: Record<string, unknown>[]
  columns: string[]
  offset: number
  limit: number
}

const PAGE_SIZE = 20

function describeReason(reason?: string): string {
  switch (reason) {
    case 'binding-missing':
      return "DO inspection unavailable — DISPATCHER binding not configured (typical in local dev). Deploy to access live SQL state."
    case 'admin-token-missing':
      return 'DO inspection unavailable — PLATFORM_ADMIN_TOKEN secret not set on the console worker.'
    case 'legacy-seed':
      return 'DO inspection unavailable — this seed is on a legacy SDK (<3.4). Upgrade the seed to enable runtime DO inspection.'
    default:
      return reason ? `DO inspection unavailable: ${reason}` : 'DO inspection unavailable.'
  }
}

export interface DOSqlBrowserProps {
  shareId: number
}

export function DOSqlBrowser({ shareId }: DOSqlBrowserProps) {
  const [probe, setProbe] = useState<ProbeResult | null>(null)
  const [probeLoading, setProbeLoading] = useState(true)
  const [probeError, setProbeError] = useState<string | null>(null)

  const [selectedTable, setSelectedTable] = useState<string | null>(null)
  const [offset, setOffset] = useState(0)
  const [page, setPage] = useState<PageResult | null>(null)
  const [count, setCount] = useState<number | null>(null)
  const [queryLoading, setQueryLoading] = useState(false)
  const [queryError, setQueryError] = useState<string | null>(null)

  function pickTable(name: string) {
    setSelectedTable(name)
    setOffset(0)
  }

  // Probe on mount.
  useEffect(() => {
    let active = true
    setProbeLoading(true)
    setProbeError(null)
    fetch(`/api/shares/${shareId}/do-sql/probe`)
      .then(async r => {
        if (!r.ok) throw new Error(`Probe failed: ${r.status}`)
        return r.json()
      })
      .then((p: ProbeResult) => {
        if (!active) return
        // Filter Cloudflare-internal tables here too — older seeds on
        // seed-sdk <3.5.2 still expose them in sqlite_master, and clicking
        // any of them returns SQLITE_AUTH. Hide them from the UI.
        const filtered = p.tables ? p.tables.filter(t => !t.name.startsWith('_cf_')) : []
        setProbe({ ...p, tables: filtered })
        if (p.available && filtered.length > 0) {
          pickTable(filtered[0].name)
        }
      })
      .catch(err => {
        if (!active) return
        setProbeError(err instanceof Error ? err.message : String(err))
      })
      .finally(() => { if (active) setProbeLoading(false) })
    return () => { active = false }
  }, [shareId])

  // Fetch row count when a table is picked (once per table).
  useEffect(() => {
    if (!selectedTable) {
      setCount(null)
      return
    }
    let active = true
    fetch(`/api/shares/${shareId}/do-sql/count?table=${encodeURIComponent(selectedTable)}`)
      .then(async r => (r.ok ? r.json() : null))
      .then((d: { count?: number } | null) => {
        if (active && d && typeof d.count === 'number') setCount(d.count)
      })
      .catch(() => { /* leave count null; pagination still works via Prev/Next bounds */ })
    return () => { active = false }
  }, [shareId, selectedTable])

  // Fetch the current page of rows.
  useEffect(() => {
    if (!selectedTable) {
      setPage(null)
      return
    }
    let active = true
    setQueryLoading(true)
    setQueryError(null)
    const params = new URLSearchParams({
      table: selectedTable,
      offset: String(offset),
      limit: String(PAGE_SIZE),
    })
    fetch(`/api/shares/${shareId}/do-sql/rows?${params}`)
      .then(async r => {
        if (!r.ok) {
          const body = await r.text()
          throw new Error(body || `Rows failed: ${r.status}`)
        }
        return r.json()
      })
      .then((res: PageResult) => { if (active) setPage(res) })
      .catch(err => { if (active) setQueryError(err instanceof Error ? err.message : String(err)) })
      .finally(() => { if (active) setQueryLoading(false) })
    return () => { active = false }
  }, [shareId, selectedTable, offset])

  if (probeLoading) {
    return <div className="space-y-2"><Skeleton className="h-8 w-full" /><Skeleton className="h-32 w-full" /></div>
  }

  if (probeError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{probeError}</AlertDescription>
      </Alert>
    )
  }

  if (!probe?.available) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{describeReason(probe?.reason)}</AlertDescription>
      </Alert>
    )
  }

  const tables = probe.tables ?? []
  if (tables.length === 0) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          The Durable Object has no user tables yet. Tables hydrate from the seed's R2 migrations on first access.
        </AlertDescription>
      </Alert>
    )
  }

  const columns = page?.columns ?? (page?.rows[0] ? Object.keys(page.rows[0]) : [])
  const totalPages = count !== null ? Math.max(1, Math.ceil(count / PAGE_SIZE)) : null
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1
  const atEnd = count !== null ? offset + PAGE_SIZE >= count : (page?.rows.length ?? 0) < PAGE_SIZE

  return (
    // min-w-0 on the right pane lets it shrink inside its grid cell so the inner
    // overflow-x-auto can actually clip wide tables instead of pushing the page.
    <div className="grid grid-cols-[200px_minmax(0,1fr)] gap-4 items-start">
      {/* Left rail: table list. Caps height to match the table panel and scrolls. */}
      <Card className="p-2 sticky top-4 max-h-[70vh] overflow-y-auto">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground px-2 py-1">
          Tables ({tables.length})
        </p>
        <ul className="space-y-0.5">
          {tables.map(t => (
            <li key={t.name}>
              <button
                onClick={() => pickTable(t.name)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1 rounded text-sm font-mono text-left hover:bg-muted/40',
                  selectedTable === t.name && 'bg-muted',
                )}
              >
                <Database className="size-3.5 text-muted-foreground shrink-0" />
                <span className="truncate">{t.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </Card>

      {/* Right pane: paginated rows. min-w-0 enables horizontal scroll on the table. */}
      <div className="space-y-2 min-w-0">
        {selectedTable && (
          <div className="flex items-center justify-between text-xs font-mono text-muted-foreground px-1">
            <span>
              <span className="text-foreground">{selectedTable}</span>
              {' — '}
              {count !== null
                ? `${count.toLocaleString()} row(s) • page ${currentPage}${totalPages ? ` / ${totalPages}` : ''}`
                : `page ${currentPage}`}
            </span>
            <div className="flex gap-1">
              <button
                className="px-2 py-1 rounded border text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={offset === 0 || queryLoading}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              >
                ← Prev
              </button>
              <button
                className="px-2 py-1 rounded border text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={atEnd || queryLoading}
                onClick={() => setOffset(offset + PAGE_SIZE)}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {queryLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : queryError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="font-mono text-xs whitespace-pre-wrap break-all">{queryError}</AlertDescription>
          </Alert>
        ) : !page || page.rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">No rows in {selectedTable}.</div>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-auto max-h-[calc(70vh-3rem)]">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    {columns.map(c => (
                      <TableHead key={c} className="text-xs font-mono whitespace-nowrap">{c}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {page.rows.map((r, i) => (
                    <TableRow key={i} className="hover:bg-muted/20">
                      {columns.map(c => {
                        const v = r[c]
                        return (
                          <TableCell key={c} className="text-sm whitespace-nowrap max-w-64 truncate">
                            {v === null || v === undefined
                              ? <span className="text-muted-foreground">NULL</span>
                              : typeof v === 'object'
                                ? <span className="font-mono text-xs text-muted-foreground">{JSON.stringify(v).slice(0, 80)}</span>
                                : String(v).length > 80 ? String(v).slice(0, 80) + '...' : String(v)}
                          </TableCell>
                        )
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}
